import React from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
} from "chart.js";
import { Bar, Line } from "react-chartjs-2";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import SentimentVeryDissatisfiedIcon from "@material-ui/icons/SentimentVeryDissatisfied";
import SentimentSatisfiedIcon from "@material-ui/icons/SentimentSatisfied";
import SentimentSatisfiedAltIcon from "@material-ui/icons/SentimentSatisfiedAlt";
import SentimentVerySatisfiedIcon from "@material-ui/icons/SentimentVerySatisfied";
import StarIcon from "@material-ui/icons/Star";
import QuestionAnswerIcon from "@material-ui/icons/QuestionAnswer";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";

import { i18n } from "../../translate/i18n";
import { getChartGridColor, getChartTextColor } from "../Dashboard/chartTheme";
import {
  GROUPS,
  formatDay,
  formatNps,
  formatNumber,
  formatPercent,
  groupColors,
  groupOf,
  npsZone
} from "./ratingHelpers";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const useStyles = makeStyles(theme => ({
  // Mesmo cartão dos contadores do Dashboard.
  card: {
    padding: theme.spacing(2),
    display: "flex",
    flexDirection: "column",
    height: "100%",
    overflow: "hidden",
    backgroundColor:
      theme.palette.type === "dark"
        ? theme.palette.boxticket.main
        : theme.palette.primary.main,
    color: "#eee"
  },
  cardRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    gap: theme.spacing(1)
  },
  cardValue: {
    fontSize: 34,
    fontWeight: 500,
    lineHeight: 1.2
  },
  cardHint: {
    fontSize: 13,
    opacity: 0.9,
    marginTop: theme.spacing(0.5)
  },
  cardIcon: {
    fontSize: 64,
    color: "#FFFFFF",
    flexShrink: 0
  },
  paper: {
    padding: theme.spacing(2),
    height: "100%"
  },
  paperTitle: {
    marginBottom: theme.spacing(0.5)
  },
  paperSubtitle: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2)
  },
  groupItem: {
    display: "flex",
    gap: theme.spacing(1.5),
    alignItems: "flex-start",
    marginBottom: theme.spacing(1.5)
  },
  groupDot: {
    width: 14,
    height: 14,
    borderRadius: "50%",
    flexShrink: 0,
    marginTop: 3
  },
  formula: {
    padding: theme.spacing(1, 1.5),
    borderRadius: 6,
    backgroundColor: theme.palette.action.hover,
    marginTop: theme.spacing(1)
  },
  stackedBar: {
    display: "flex",
    gap: 2,
    height: 28,
    borderRadius: 4,
    overflow: "hidden",
    backgroundColor: theme.palette.action.hover
  },
  stackedLegend: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    marginTop: theme.spacing(1),
    fontSize: 13
  },
  legendItem: {
    display: "inline-flex",
    alignItems: "center",
    gap: 6
  },
  legendSwatch: {
    width: 10,
    height: 10,
    borderRadius: 2
  },
  scaleTrack: {
    position: "relative",
    height: 10,
    borderRadius: 5,
    backgroundColor: theme.palette.action.selected,
    margin: theme.spacing(3, 0, 1)
  },
  scaleTick: {
    position: "absolute",
    top: -4,
    width: 2,
    height: 18,
    backgroundColor: theme.palette.text.disabled
  },
  scaleMarker: {
    position: "absolute",
    top: -7,
    width: 24,
    height: 24,
    marginLeft: -12,
    borderRadius: "50%",
    border: `3px solid ${theme.palette.background.paper}`,
    backgroundColor: theme.palette.text.primary
  },
  scaleLabels: {
    position: "relative",
    height: 34,
    fontSize: 12,
    color: theme.palette.text.secondary
  },
  scaleLabel: {
    position: "absolute",
    transform: "translateX(-50%)",
    textAlign: "center",
    whiteSpace: "nowrap"
  },
  chartBox: {
    height: 260
  },
  empty: {
    color: theme.palette.text.secondary,
    padding: theme.spacing(4, 0),
    textAlign: "center"
  },
  tableWrapper: {
    overflowX: "auto"
  },
  miniBar: {
    display: "flex",
    gap: 2,
    height: 10,
    width: 120,
    borderRadius: 3,
    overflow: "hidden",
    backgroundColor: theme.palette.action.hover
  }
}));

// O rosto do cartão do NPS acompanha a faixa (sem respostas = neutro).
const NPS_ZONE_ICONS = {
  critical: SentimentVeryDissatisfiedIcon,
  improvement: SentimentSatisfiedIcon,
  quality: SentimentSatisfiedAltIcon,
  excellence: SentimentVerySatisfiedIcon
};

const SummaryCard = ({ classes, title, value, hint, Icon }) => (
  <Paper className={classes.card} elevation={4}>
    <div className={classes.cardRow}>
      <div>
        <Typography component="h3" variant="h6">
          {title}
        </Typography>
        <div className={classes.cardValue}>{value}</div>
        {hint && <div className={classes.cardHint}>{hint}</div>}
      </div>
      <Icon className={classes.cardIcon} />
    </div>
  </Paper>
);

const GroupSplit = ({ classes, metrics, colors }) => {
  const parts = GROUPS.map(group => ({
    group,
    total: metrics[`${group}s`],
    percent: metrics.answered ? (metrics[`${group}s`] / metrics.answered) * 100 : 0
  }));

  return (
    <>
      <div className={classes.stackedBar}>
        {parts
          .filter(part => part.total > 0)
          .map(part => (
            <div
              key={part.group}
              title={`${i18n.t(`ticketRatings.groups.${part.group}.name`)}: ${
                part.total
              } (${formatPercent(part.percent)})`}
              style={{
                width: `${part.percent}%`,
                backgroundColor: colors[part.group]
              }}
            />
          ))}
      </div>
      <div className={classes.stackedLegend}>
        {parts.map(part => (
          <span key={part.group} className={classes.legendItem}>
            <span
              className={classes.legendSwatch}
              style={{ backgroundColor: colors[part.group] }}
            />
            {i18n.t(`ticketRatings.groups.${part.group}.name`)}:{" "}
            <strong>{formatPercent(part.percent)}</strong> ({part.total})
          </span>
        ))}
      </div>
    </>
  );
};

// Régua de −100 a +100 com as faixas de leitura do NPS e o ponto da empresa.
const NpsScale = ({ classes, nps }) => {
  const toLeft = value => `${((value + 100) / 200) * 100}%`;
  const ticks = [0, 50, 75];
  const labels = [
    { at: -50, zone: "critical" },
    { at: 25, zone: "improvement" },
    { at: 62.5, zone: "quality" },
    { at: 87.5, zone: "excellence" }
  ];

  return (
    <>
      <div className={classes.scaleTrack}>
        {ticks.map(tick => (
          <span
            key={tick}
            className={classes.scaleTick}
            style={{ left: toLeft(tick) }}
          />
        ))}
        {nps !== null && (
          <span
            className={classes.scaleMarker}
            style={{ left: toLeft(Math.max(-100, Math.min(100, nps))) }}
            title={`NPS ${formatNps(nps)}`}
          />
        )}
      </div>
      <div className={classes.scaleLabels}>
        {labels.map(label => (
          <span
            key={label.zone}
            className={classes.scaleLabel}
            style={{
              left: toLeft(label.at),
              fontWeight: npsZone(nps) === label.zone ? 700 : 400
            }}
          >
            {i18n.t(`ticketRatings.zones.${label.zone}`)}
          </span>
        ))}
      </div>
    </>
  );
};

const DistributionChart = ({ distribution, scale, colors, textColor, gridColor }) => {
  const data = {
    labels: distribution.map(d => String(d.rate)),
    datasets: [
      {
        data: distribution.map(d => d.total),
        backgroundColor: distribution.map(d => colors[groupOf(d.rate, scale)]),
        borderRadius: { topLeft: 4, topRight: 4 },
        borderSkipped: "bottom",
        maxBarThickness: 36
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      // A cor é do grupo, não de uma série: a legenda lista os três grupos.
      legend: {
        display: true,
        position: "top",
        onClick: () => {},
        labels: {
          color: textColor,
          generateLabels: () =>
            GROUPS.map(group => ({
              text: i18n.t(`ticketRatings.groups.${group}.label`),
              fillStyle: colors[group],
              strokeStyle: colors[group],
              fontColor: textColor
            }))
        }
      },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          title: items =>
            i18n.t("ticketRatings.charts.rateTooltip", {
              rate: items[0].label
            }),
          label: item =>
            `${i18n.t("ticketRatings.charts.responses", {
              count: item.raw
            })} · ${i18n.t(
              `ticketRatings.groups.${groupOf(Number(item.label), scale)}.name`
            )}`
        }
      }
    },
    scales: {
      x: {
        ticks: { color: textColor },
        grid: { display: false },
        title: {
          display: true,
          text: i18n.t("ticketRatings.charts.rateAxis"),
          color: textColor
        }
      },
      y: {
        beginAtZero: true,
        ticks: { color: textColor, precision: 0 },
        grid: { color: gridColor }
      }
    }
  };

  return <Bar data={data} options={options} />;
};

const TimelineChart = ({ timeline, scale, lineColor, textColor, gridColor }) => {
  const data = {
    labels: timeline.map(d => formatDay(d.date)),
    datasets: [
      {
        data: timeline.map(d => (d.average === null ? null : Number(d.average.toFixed(1)))),
        borderColor: lineColor,
        backgroundColor: lineColor,
        borderWidth: 2,
        pointRadius: 4,
        pointHoverRadius: 6,
        spanGaps: true,
        tension: 0.25
      }
    ]
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: { mode: "index", intersect: false },
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        filter: item => item.raw !== null,
        callbacks: {
          label: item =>
            `${i18n.t("ticketRatings.charts.average")}: ${formatNumber(item.raw)} · ${i18n.t(
              "ticketRatings.charts.responses",
              { count: timeline[item.dataIndex].answered }
            )}`
        }
      }
    },
    scales: {
      x: { ticks: { color: textColor, maxRotation: 0, autoSkip: true }, grid: { display: false } },
      y: {
        min: scale.min,
        max: scale.max,
        ticks: { color: textColor, stepSize: 2 },
        grid: { color: gridColor }
      }
    }
  };

  return <Line data={data} options={options} />;
};

const RatingsOverview = ({ report }) => {
  const classes = useStyles();
  const theme = useTheme();
  const isDark = theme.palette.type === "dark";
  const colors = groupColors(theme);
  const textColor = getChartTextColor(isDark);
  const gridColor = getChartGridColor(isDark);
  const lineColor = isDark ? "#4FC3F7" : "#0277BD";

  const { summary, scale } = report;
  const zone = npsZone(summary.nps);
  const hasAnswers = summary.answered > 0;

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard
          classes={classes}
          title={i18n.t("ticketRatings.cards.nps")}
          value={formatNps(summary.nps)}
          hint={
            zone
              ? i18n.t(`ticketRatings.zones.${zone}`)
              : i18n.t("ticketRatings.cards.noAnswers")
          }
          Icon={NPS_ZONE_ICONS[zone] || SentimentSatisfiedIcon}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard
          classes={classes}
          title={i18n.t("ticketRatings.cards.average")}
          value={formatNumber(summary.average)}
          hint={i18n.t("ticketRatings.cards.averageHint", { max: scale.max })}
          Icon={StarIcon}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard
          classes={classes}
          title={i18n.t("ticketRatings.cards.responseRate")}
          value={formatPercent(summary.responseRate)}
          hint={i18n.t("ticketRatings.cards.responseRateHint", {
            answered: summary.answered,
            sent: summary.sent
          })}
          Icon={QuestionAnswerIcon}
        />
      </Grid>
      <Grid item xs={12} sm={6} md={3}>
        <SummaryCard
          classes={classes}
          title={i18n.t("ticketRatings.cards.noAnswer")}
          value={summary.noAnswer}
          hint={i18n.t("ticketRatings.cards.waitingHint", {
            total: summary.waiting
          })}
          Icon={HourglassEmptyIcon}
        />
      </Grid>

      {/* Logo abaixo dos cartões, a pedido: é o que o gestor procura primeiro. */}
      <Grid item xs={12}>
        <Paper className={classes.paper}>
          <Typography variant="h6" color="primary" className={classes.paperTitle}>
            {i18n.t("ticketRatings.ranking.title")}
          </Typography>
          <Typography variant="body2" className={classes.paperSubtitle}>
            {i18n.t("ticketRatings.ranking.subtitle")}
          </Typography>
          <div className={classes.tableWrapper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{i18n.t("ticketRatings.table.user")}</TableCell>
                  <TableCell align="right">{i18n.t("ticketRatings.ranking.sent")}</TableCell>
                  <TableCell align="right">{i18n.t("ticketRatings.ranking.answered")}</TableCell>
                  <TableCell align="right">{i18n.t("ticketRatings.cards.average")}</TableCell>
                  <TableCell align="right">{i18n.t("ticketRatings.cards.nps")}</TableCell>
                  <TableCell>{i18n.t("ticketRatings.ranking.split")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {report.byUser.map(row => (
                  <TableRow key={`${row.userId}-${row.userName}`}>
                    <TableCell>{row.userName || "—"}</TableCell>
                    <TableCell align="right">{row.sent}</TableCell>
                    <TableCell align="right">
                      {row.answered} ({formatPercent(row.responseRate)})
                    </TableCell>
                    <TableCell align="right">{formatNumber(row.average)}</TableCell>
                    <TableCell align="right">
                      <strong>{formatNps(row.nps)}</strong>
                      {row.nps !== null && (
                        <Typography variant="caption" color="textSecondary" display="block">
                          {i18n.t(`ticketRatings.zones.${npsZone(row.nps)}`)}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {row.answered > 0 ? (
                        <div
                          className={classes.miniBar}
                          title={GROUPS.map(
                            group =>
                              `${i18n.t(`ticketRatings.groups.${group}.name`)}: ${row[`${group}s`]}`
                          ).join(" · ")}
                        >
                          {GROUPS.filter(group => row[`${group}s`] > 0).map(group => (
                            <div
                              key={group}
                              style={{
                                width: `${(row[`${group}s`] / row.answered) * 100}%`,
                                backgroundColor: colors[group]
                              }}
                            />
                          ))}
                        </div>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {report.byUser.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {i18n.t("ticketRatings.table.empty")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </Paper>
      </Grid>

      <Grid item xs={12} md={5}>
        <Paper className={classes.paper}>
          <Typography variant="h6" color="primary" className={classes.paperTitle}>
            {i18n.t("ticketRatings.howToRead.title")}
          </Typography>
          <Typography variant="body2" className={classes.paperSubtitle}>
            {i18n.t("ticketRatings.howToRead.subtitle", { max: scale.max })}
          </Typography>
          {GROUPS.map(group => (
            <div key={group} className={classes.groupItem}>
              <span
                className={classes.groupDot}
                style={{ backgroundColor: colors[group] }}
              />
              <div>
                <Typography variant="body2">
                  <strong>{i18n.t(`ticketRatings.groups.${group}.label`)}</strong>
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {i18n.t(`ticketRatings.groups.${group}.description`)}
                </Typography>
              </div>
            </div>
          ))}
          <div className={classes.formula}>
            <Typography variant="body2">
              {i18n.t("ticketRatings.howToRead.formula")}
            </Typography>
          </div>
        </Paper>
      </Grid>

      <Grid item xs={12} md={7}>
        <Paper className={classes.paper}>
          <Typography variant="h6" color="primary" className={classes.paperTitle}>
            {i18n.t("ticketRatings.npsBlock.title")}
          </Typography>
          <Typography variant="body2" className={classes.paperSubtitle}>
            {hasAnswers
              ? i18n.t("ticketRatings.npsBlock.subtitle", {
                  nps: formatNps(summary.nps),
                  zone: i18n.t(`ticketRatings.zones.${zone}`),
                  total: summary.answered
                })
              : i18n.t("ticketRatings.cards.noAnswers")}
          </Typography>
          <NpsScale classes={classes} nps={hasAnswers ? summary.nps : null} />
          <Typography variant="subtitle2" style={{ marginTop: 8 }}>
            {i18n.t("ticketRatings.npsBlock.split")}
          </Typography>
          <div style={{ marginTop: 8 }}>
            <GroupSplit classes={classes} metrics={summary} colors={colors} />
          </div>
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper className={classes.paper}>
          <Typography variant="h6" color="primary" className={classes.paperTitle}>
            {i18n.t("ticketRatings.charts.distributionTitle")}
          </Typography>
          <Typography variant="body2" className={classes.paperSubtitle}>
            {i18n.t("ticketRatings.charts.distributionSubtitle")}
          </Typography>
          {hasAnswers ? (
            <div className={classes.chartBox}>
              <DistributionChart
                distribution={report.distribution}
                scale={scale}
                colors={colors}
                textColor={textColor}
                gridColor={gridColor}
              />
            </div>
          ) : (
            <div className={classes.empty}>{i18n.t("ticketRatings.cards.noAnswers")}</div>
          )}
        </Paper>
      </Grid>

      <Grid item xs={12} md={6}>
        <Paper className={classes.paper}>
          <Typography variant="h6" color="primary" className={classes.paperTitle}>
            {i18n.t("ticketRatings.charts.timelineTitle")}
          </Typography>
          <Typography variant="body2" className={classes.paperSubtitle}>
            {i18n.t("ticketRatings.charts.timelineSubtitle")}
          </Typography>
          {hasAnswers ? (
            <div className={classes.chartBox}>
              <TimelineChart
                timeline={report.timeline}
                scale={scale}
                lineColor={lineColor}
                textColor={textColor}
                gridColor={gridColor}
              />
            </div>
          ) : (
            <div className={classes.empty}>{i18n.t("ticketRatings.cards.noAnswers")}</div>
          )}
        </Paper>
      </Grid>

    </Grid>
  );
};

export default RatingsOverview;
